/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_COMPANY_INFO_COMPONENT
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Company information component for broadcast details page displaying
 * seller/company contact details. Based on CompanyInformation pattern.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Projected: ~190 lines
 * - Decision: Proceed (within 200-line limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcast-details/BroadcastDetailsPage.tsx
 * - client/src/features/broadcast/pages/broadcast-details/interfaces/broadcastDetail.ts
 */

import React from "react";
import { BroadcastUser } from "../interfaces/broadcastDetail";
import styles from "./BroadcastCompanyInfo.module.scss";

// [2025-12-29] Broadcast details: Company/seller information display

interface BroadcastCompanyInfoProps {
  user: BroadcastUser;
}

const BroadcastCompanyInfo: React.FC<BroadcastCompanyInfoProps> = ({ user }) => {
  // [2025-12-29] Format contact person name as "FirstName LastInitial"
  const contactName = user.lastName
    ? `${user.firstName} ${user.lastName.charAt(0).toUpperCase()}.`
    : user.firstName;
  const companyName = user.companyName || contactName;

  // [2025-12-29] Format website URL
  const formatWebsite = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  return (
    <div className={styles["broadcast-company-info"]}>
      <div className={styles["broadcast-company-info__header"]}>
        <h2 className={styles["broadcast-company-info__title"]}>
          <span>{companyName}</span>
        </h2>
      </div>
      <div className={styles["broadcast-company-info__divider"]}>
        <div className={styles["broadcast-company-info__divider-line"]} />
      </div>

      <div className={styles["broadcast-company-info__content"]}>
        {/* Main Contact */}
        <div className={styles["broadcast-company-info__field"]}>
          <svg
            className={styles["broadcast-company-info__icon"]}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 10a5 5 0 100-10 5 5 0 000 10zm0 2.5c-3.33 0-10 1.67-10 5v2.5h20V17.5c0-3.33-6.67-5-10-5z"
              fill="currentColor"
            />
          </svg>
          <span className={styles["broadcast-company-info__label"]}>Main contact:</span>
          <span className={styles["broadcast-company-info__text"]}>{contactName}</span>
        </div>

        {/* Website */}
        {user.website && (
          <div className={styles["broadcast-company-info__field"]}>
            <svg
              className={styles["broadcast-company-info__icon"]}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm7.931 9h-3.182A12.633 12.633 0 0014 5.186 8.03 8.03 0 0117.931 9zM10 17.931c-.905 0-2.5-2.545-2.864-6.931h5.728C12.5 15.386 10.905 17.931 10 17.931zM7.136 9C7.5 4.614 9.095 2.069 10 2.069c.905 0 2.5 2.545 2.864 6.931H7.136zM6 5.186A12.633 12.633 0 005.251 9H2.069A8.03 8.03 0 016 5.186zM2.069 11h3.182A12.633 12.633 0 006 14.814 8.03 8.03 0 012.069 11zm11.931 3.814A12.633 12.633 0 0014.749 11h3.182A8.03 8.03 0 0114 14.814z"
                fill="currentColor"
              />
            </svg>
            <a
              href={formatWebsite(user.website)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles["broadcast-company-info__link"]}
            >
              {user.website}
            </a>
          </div>
        )}

        {/* Email */}
        {(user.companyEmail || user.email) && (
          <div className={styles["broadcast-company-info__field"]}>
            <svg
              className={styles["broadcast-company-info__icon"]}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 4H2C.9 4 .01 4.9.01 6L0 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                fill="currentColor"
              />
            </svg>
            <a
              href={`mailto:${user.companyEmail || user.email}`}
              className={styles["broadcast-company-info__link"]}
            >
              {user.companyEmail || user.email}
            </a>
          </div>
        )}

        {/* Phone */}
        {user.telCompany && (
          <div className={styles["broadcast-company-info__field"]}>
            <svg
              className={styles["broadcast-company-info__icon"]}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.364 15.636l-3.536-3.536a1 1 0 00-1.414 0l-1.414 1.414a1 1 0 01-1.414 0l-5.657-5.657a1 1 0 010-1.414l1.414-1.414a1 1 0 000-1.414L2.808 0.08a1 1 0 00-1.414 0L.08 1.394A10 10 0 0015.85 17.142l1.314-1.314a1 1 0 000-1.414l-3.536-3.536z"
                fill="currentColor"
              />
            </svg>
            <a href={`tel:${user.telCompany}`} className={styles["broadcast-company-info__link"]}>
              {user.telCompany}
            </a>
          </div>
        )}

        {/* Country */}
        {user.country && (
          <div className={styles["broadcast-company-info__field"]}>
            <svg
              className={styles["broadcast-company-info__icon"]}
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 0C6.13 0 3 3.13 3 7c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"
                fill="currentColor"
              />
            </svg>
            <span className={styles["broadcast-company-info__text"]}>{user.country}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BroadcastCompanyInfo;
